import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Eye, TrendingUp, Smartphone, RefreshCw, AlertCircle, Users, MousePointerClick } from 'lucide-react';
import SummaryCard from '../components/dashboard/SummaryCard';
import DateRangePicker from '../components/dashboard/DateRangePicker';
import RevenueChart from '../components/dashboard/RevenueChart';
import { analyticsService } from '../services/analyticsService';
import { syncService } from '../services/syncService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Dashboard Overview Page
 * Displays summary statistics and overview metrics
 */
const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [chartData, setChartData] = useState(null);

  // Date range state (defaults to last 30 days)
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  });

  // Account ID state (optional, defaults to user's default account)
  const [accountId, setAccountId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Fetch overview data - memoized to prevent stale closures
  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
      };

      if (accountId) {
        params.account_id = accountId;
      }

      // Fetch overview and chart data in parallel
      const [overviewResponse, chartResponse] = await Promise.all([
        analyticsService.getOverview(params),
        analyticsService.getRevenueChart({ ...params, period: 'daily' })
      ]);

      setOverview(overviewResponse.data);
      setChartData(chartResponse.data);
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load dashboard overview';
      setError(errorMessage);
      console.error('Dashboard overview error:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, accountId]);


  // Load sync status
  const loadSyncStatus = async () => {
    try {
      const syncStatusRes = await syncService.getSyncStatus(accountId, 'revenue');
      if (syncStatusRes.data?.latest?.revenue) {
        const lastSync = syncStatusRes.data.latest.revenue;
        const syncTime = new Date(lastSync.completed_at || lastSync.started_at);
        setLastSyncTime(syncTime);
        return syncTime;
      } else if (syncStatusRes.data?.statistics?.by_type?.revenue?.last_successful_sync) {
        const syncTime = new Date(syncStatusRes.data.statistics.by_type.revenue.last_successful_sync);
        setLastSyncTime(syncTime);
        return syncTime;
      }
      return null;
    } catch (err) {
      console.error('Error loading sync status:', err);
      return null;
    }
  };

  // Manual sync handler
  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncError(null);

      const result = await syncService.fetchHistoricalData(
        dateRange.startDate,
        dateRange.endDate,
        accountId
      );

      if (result.data?.sync_result) {
        setLastSyncTime(new Date());
        await loadSyncStatus();
        await fetchOverview();
      }
    } catch (err) {
      console.error('Sync error:', err);
      setSyncError(err.message || 'Failed to sync data');
    } finally {
      setSyncing(false);
    }
  };

  // Format last sync time
  const formatLastSyncTime = () => {
    if (!lastSyncTime) return 'Never synced';
    const now = new Date();
    const diffMs = now - lastSyncTime;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // On page load: Check sync status and auto-sync if data is older than 15 minutes
  useEffect(() => {
    const initializeSync = async () => {
      try {
        // Load sync status first
        const syncTime = await loadSyncStatus();

        // Auto-sync if data is older than 15 minutes
        if (syncTime) {
          const minutesSinceSync = (Date.now() - syncTime.getTime()) / (1000 * 60);
          if (minutesSinceSync > 15) {
            console.log(`Data is ${Math.round(minutesSinceSync)} minutes old. Auto-syncing...`);
            await handleSync();
          }
        } else {
          // No sync history, sync now
          console.log('No sync history found. Syncing...');
          await handleSync();
        }
      } catch (err) {
        console.error('Error initializing sync:', err);
        // Don't block page load if sync check fails
      }
    };

    initializeSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  useEffect(() => {
    fetchOverview();
  }, [dateRange.startDate, dateRange.endDate, accountId, fetchOverview]);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  const isDataEmpty = (overview?.summary) && (
    (overview.summary.total_revenue ?? 0) === 0 &&
    (overview.summary.today_earnings ?? 0) === 0 &&
    (overview.summary.total_impressions ?? 0) === 0 &&
    (overview.summary.total_clicks ?? 0) === 0 &&
    (overview.summary.active_apps ?? 0) === 0 &&
    (overview.summary.fill_rate ?? 0) === 0 &&
    (overview.summary.active_users ?? 0) === 0
  );

  const handleSyncFromIronSource = async () => {
    await handleSync();
  };

  if (loading && !overview) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center h-40">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-xs sm:text-sm text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Card className="border-destructive">
          <CardContent className="pt-3 pb-3">
            <p className="text-xs sm:text-sm text-destructive mb-2">{error}</p>
            <Button
              onClick={fetchOverview}
              variant="destructive"
              size="sm"
              className="text-xs"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const summary = overview?.summary || {};
  const dateRangeInfo = overview?.date_range || {};
  const dateRangeDisplay = dateRangeInfo.start_date && dateRangeInfo.end_date
    ? `${dateRangeInfo.start_date} - ${dateRangeInfo.end_date}`
    : `${dateRange.startDate} - ${dateRange.endDate}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="mb-2 sm:mb-3 lg:mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1">
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Dashboard Overview</h1>
            <p className="text-muted-foreground text-xs">
              {overview?.account_name && (
                <span className="block sm:inline">
                  Account: {overview.account_name}
                </span>
              )}
              {lastSyncTime && (
                <span className="block sm:inline sm:ml-2 text-xs mt-0.5 sm:mt-0">
                  • Data synced {formatLastSyncTime()}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button 
              onClick={handleSync} 
              variant="outline" 
              size="sm"
              disabled={syncing || loading}
              title="Sync data from IronSource"
              className="text-xs"
            >
              <RefreshCw className={`w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
            {syncError && (
              <span className="text-xs text-destructive" title={syncError}>
                Sync failed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* No data - prompt to sync */}
      {isDataEmpty && (
        <Alert className="mb-2 border-amber-500/50 bg-amber-500/5 dark:bg-amber-500/15">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <AlertDescription className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-1.5">
            <span className="flex-1 text-xs sm:text-sm">No data yet. Sync from IronSource to load your apps and revenue for the selected date range.</span>
            <Button
              size="sm"
              onClick={handleSyncFromIronSource}
              disabled={syncing}
              className="w-full sm:w-auto text-xs"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync from IronSource'}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {syncError && (
        <Alert variant="destructive" className="mb-2">
          <AlertCircle className="h-3.5 w-3.5" />
          <AlertDescription className="text-xs sm:text-sm">{syncError}</AlertDescription>
        </Alert>
      )}

      {/* Date Range Picker */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm sm:text-base">Date Range</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={handleDateRangeChange}
          />
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
        <SummaryCard
          title="Today's Earnings"
          value={summary.today_earnings || 0}
          subtitle="Today"
          icon={DollarSign}
          isCurrency={true}
        />
        <SummaryCard
          title="Total Revenue"
          value={summary.total_revenue || 0}
          subtitle={dateRangeDisplay}
          icon={DollarSign}
          isCurrency={true}
        />
        <SummaryCard
          title="Total Impressions"
          value={summary.total_impressions || 0}
          subtitle={dateRangeDisplay}
          icon={Eye}
          isCurrency={false}
        />
        <SummaryCard
          title="Total Clicks"
          value={summary.total_clicks || 0}
          subtitle={dateRangeDisplay}
          icon={MousePointerClick}
          isCurrency={false}
        />
        <SummaryCard
          title="Average eCPM"
          value={summary.average_ecpm || 0}
          subtitle={dateRangeDisplay}
          icon={TrendingUp}
          isCurrency={true}
        />
        <SummaryCard
          title="Fill Rate"
          value={summary.fill_rate || 0}
          subtitle="Fill rate percentage"
          icon={TrendingUp}
          isCurrency={false}
          isPercentage={true}
        />
        <SummaryCard
          title="Daily Active Users"
          value={summary.active_users || 0}
          subtitle="Average DAU"
          icon={Users}
          isCurrency={false}
        />
        <SummaryCard
          title="Active Apps"
          value={summary.active_apps || 0}
          subtitle="Currently active"
          icon={Smartphone}
          isCurrency={false}
        />
      </div>

      {/* Revenue Chart */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm sm:text-base">Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <RevenueChart 
            data={chartData?.data || []} 
            type="line" 
            height={220}
            showLegend={true}
          />
        </CardContent>
      </Card>

      {/* Additional content can be added here */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm sm:text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <Button asChild variant="outline" size="sm" className="w-full text-xs sm:text-sm">
              <Link to="/analytics">
                View Detailed Analytics
              </Link>
            </Button>
            <Button
              onClick={() => window.location.href = '/apps'}
              variant="outline"
              size="sm"
              className="w-full text-xs sm:text-sm"
            >
              Manage Apps
            </Button>
            <Button
              onClick={fetchOverview}
              variant="default"
              size="sm"
              className="w-full text-xs sm:text-sm"
            >
              Refresh Data
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
